<template>
  <div
    v-if="isLoading"
    class="h-72 w-48 animate-pulse rounded-md bg-slate-100"
  />

  <p
    v-else-if="!imageUrl"
    class="text-sm text-slate-500"
  >
    {{ $t('share_image_unavailable', 'There\'s no image for this page, so use one of your own.') }}
  </p>

  <div v-else>
    <div
      v-if="hasMultipleImageAspectRatios"
      class="mb-4 flex items-center gap-x-1.5"
    >
      <Button
        v-for="option in aspectRatioOptions"
        :key="option.value"
        size="sm"
        variant="outline"
        color="gray"
        :ui="{
          base: [
            'p-0 size-10 flex items-center justify-center ring-1',
            imageAspectRatio === option.value
              ? 'ring-primary text-primary bg-primary-50 hover:ring-primary hover:text-primary hover:bg-primary-50 hover:cursor-default'
              : 'hover:text-primary hover:ring-primary'
          ]
        }"
        @click="imageAspectRatio = option.value"
      >
        <Icon
          :icon="option.icon"
          prefix="far"
        />
      </Button>
    </div>

    <img
      :src="imageUrl"
      alt="Social image"
      class="max-h-[28rem] max-w-full rounded-md"
    />

    <div class="mt-3">
      <Button
        as="a"
        :href="imageDownloadUrl"
        prepend-icon="cloud-arrow-down"
        append-icon-prefix="fas"
        color="pink"
      >
        {{ $t('download_photo') }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IconName } from '@fortawesome/fontawesome-svg-core'
import type { LandingPage } from '~/types/landing-page'

type ImageAspectRatio = 'horizontal' | 'square' | 'vertical'

const props = defineProps<{
  media?: LandingPage['media'] | null
  preferredAspectRatio?: ImageAspectRatio
  isLoading?: boolean
}>()

const imageAspectRatio = ref<ImageAspectRatio>()

// Media can arrive after mounting, so the initial choice waits for it
watch(() => props.media, () => {
  const isAspectRatioAvailable: Record<ImageAspectRatio, boolean> = {
    vertical: !!props.media?.social_vertical_image_url,
    horizontal: !!(props.media?.social_horizontal_image_url || props.media?.header_background_image_url),
    square: !!props.media?.social_square_image_url
  }

  if (props.preferredAspectRatio && isAspectRatioAvailable[props.preferredAspectRatio]) {
    imageAspectRatio.value = props.preferredAspectRatio
  } else if (props.media?.social_vertical_image_url) {
    imageAspectRatio.value = 'vertical'
  } else if (props.media?.social_horizontal_image_url) {
    imageAspectRatio.value = 'horizontal'
  } else if (props.media?.social_square_image_url) {
    imageAspectRatio.value = 'square'
  }
}, { immediate: true })

const aspectRatioOptions = computed(() => {
  const options: { value: ImageAspectRatio, icon: IconName }[] = []

  if (props.media?.social_vertical_image_url) {
    options.push({ value: 'vertical', icon: 'rectangle-vertical' })
  }

  if (props.media?.social_horizontal_image_url || props.media?.header_background_image_url) {
    options.push({ value: 'horizontal', icon: 'rectangle' })
  }

  if (props.media?.social_square_image_url) {
    options.push({ value: 'square', icon: 'square' })
  }

  return options
})

const hasMultipleImageAspectRatios = computed(() => {
  return (
    [
      props.media?.header_background_image_url,
      props.media?.social_vertical_image_url,
      props.media?.social_horizontal_image_url,
      props.media?.social_square_image_url
    ].filter(Boolean).length > 1
  )
})

const imageUrl = computed(() => {
  if (props.media?.social_vertical_image_url && imageAspectRatio.value === 'vertical') {
    return props.media?.social_vertical_image_url
  } else if (
    (props.media?.social_horizontal_image_url || props.media?.header_background_image_url)
    && imageAspectRatio.value === 'horizontal'
  ) {
    return props.media?.social_horizontal_image_url || props.media?.header_background_image_url
  } else if (props.media?.social_square_image_url && imageAspectRatio.value === 'square') {
    return props.media?.social_square_image_url
  } else if (props.media?.header_background_image_url) {
    return props.media?.header_background_image_url
  }

  return null
})

const imageDownloadUrl = computed(() => {
  if (props.media?.social_vertical_image_download_url && imageAspectRatio.value === 'vertical') {
    return props.media?.social_vertical_image_download_url
  } else if (props.media?.social_horizontal_image_download_url && imageAspectRatio.value === 'horizontal') {
    return props.media?.social_horizontal_image_download_url || props.media?.header_background_image_download_url
  } else if (props.media?.social_square_image_download_url && imageAspectRatio.value === 'square') {
    return props.media?.social_square_image_download_url
  } else if (props.media?.header_background_image_download_url) {
    return props.media?.header_background_image_download_url
  }

  return null
})
</script>
